import { useMemo, useState } from "react";
import { formatINR } from "@/lib/brand";
import YearBreakdown from "./YearBreakdown";
import CalculatorActions from "./CalculatorActions";

export default function CagrCalculator() {
  const [initial, setInitial] = useState(100000);
  const [final, setFinal] = useState(250000);
  const [years, setYears] = useState(5);

  const { cagr, rows } = useMemo(() => {
    const p = Number(initial) || 0;
    const f = Number(final) || 0;
    const yrs = Number(years) || 0;
    let cagr = 0;
    if (p > 0 && f > 0 && yrs > 0) {
      cagr = (Math.pow(f / p, 1 / yrs) - 1) * 100;
    }
    const r = cagr / 100;
    const rows = [];
    for (let k = 1; k <= yrs; k++) {
      const vk = p * Math.pow(1 + r, k);
      rows.push({
        year: k,
        primary: vk,
        columns: [{ value: formatINR(vk) }],
      });
    }
    return { cagr, rows };
  }, [initial, final, years]);

  return (
    <div>
      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5 space-y-6">
          <NumField label="Initial Value (₹)" value={initial} onChange={setInitial} min={0} testId="cagr-initial" />
          <NumField label="Final Value (₹)" value={final} onChange={setFinal} min={0} testId="cagr-final" />
          <SliderField label="Duration (years)" value={years} onChange={setYears} min={1} max={40} testId="cagr-years" />
        </div>

        <div className="lg:col-span-7 card-paper p-6 md:p-8 ledger-rule" data-testid="cagr-result">
          <div className="eyebrow mb-2">Compound Annual Growth Rate</div>
          <div className="text-[38px] md:text-[48px] leading-none text-ink num transition-all" data-testid="cagr-value">
            {isFinite(cagr) ? cagr.toFixed(2) : "0.00"}%
          </div>

          <div className="mt-8 divide-y hairline border-t hairline border-b">
            <Row label="Initial Value" value={formatINR(initial)} />
            <Row label="Final Value" value={formatINR(final)} />
            <Row label="Duration" value={`${years} yrs`} accent />
          </div>

          {Number(initial) > 0 && Number(final) > 0 && years > 0 && (
            <p className="mt-6 text-[14px] text-ink-soft leading-relaxed">
              Your money grew at about{" "}
              <span className="num text-brass-deep font-medium">{cagr.toFixed(2)}%</span> per year,
              compounded, over{" "}
              <span className="num text-ink">{years} years</span>.
            </p>
          )}
        </div>
      </div>

      <YearBreakdown
        rows={rows}
        primaryLabel="Illustrative smooth path from initial to final value"
        tableHeaders={["Year", "Illustrative Value"]}
        testIdPrefix="cagr"
      />

      <p className="mt-4 text-[12px] text-ink-soft/85 leading-relaxed max-w-3xl">
        This shows the smooth path implied by a constant annual growth rate — actual year-to-year
        returns are almost never this even in real markets.
      </p>

      <CalculatorActions
        testIdPrefix="cagr"
        withStrategyCta
        buildPayload={() => ({
          type: "cagr",
          title: "CAGR Calculator",
          one_liner: `a CAGR calculation — ${formatINR(initial)} grew to ${formatINR(final)} over ${years} years, i.e. ${cagr.toFixed(2)}% per year compounded`,
          inputs: [
            { label: "Initial Value", value: formatINR(initial) },
            { label: "Final Value", value: formatINR(final) },
            { label: "Duration", value: `${years} years` },
          ],
          hero: { label: "Compound Annual Growth Rate", value: `${cagr.toFixed(2)}%` },
          summary: [
            { label: "Initial Value", value: formatINR(initial) },
            { label: "Final Value", value: formatINR(final), accent: true },
            { label: "Duration", value: `${years} years` },
          ],
          row_headers: ["Year", "Illustrative Value"],
          rows: rows.map((r) => [String(r.year), formatINR(r.primary)]),
        })}
      />
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between py-3">
      <span className="eyebrow">{label}</span>
      <span className={`num text-[16px] ${accent ? "text-brass-deep" : "text-ink"}`}>{value}</span>
    </div>
  );
}
function NumField({ label, value, onChange, step = 1, min, max, testId }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}</span>
      <input
        type="number" value={value} step={step} min={min} max={max}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="fld num" data-testid={testId}
      />
    </label>
  );
}
function SliderField({ label, value, onChange, min, max, testId }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-2">
        <span className="eyebrow">{label}</span>
        <span className="num text-ink text-[14px]">{value} yrs</span>
      </div>
      <input
        type="range" min={min} max={max} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[#14A5A5]" data-testid={testId}
      />
      <div className="flex justify-between text-[11px] text-ink-soft/80 mt-1 num">
        <span>{min}</span><span>{max}</span>
      </div>
    </label>
  );
}
